import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { useToast } from '../ui/Toast';
import { formatEUR, formatDate } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  Modal,
  labelCls,
  apiMessage,
  cmd,
  type Cust,
  type Mode,
  type InvoiceStatus,
} from './collectionShared';

/**
 * "Enregistrer un règlement" — the prototype's payment dialog. The modes offered depend on the
 * customer (a garage on account: transfer, LCR, direct debit, cheque; a private customer: card,
 * cash, transfer, cheque; the counter: card or cash only). An LCR or a direct debit asks for its
 * due date and only covers the invoices "en cours" until it matures. The amount is spread over the
 * open invoices, oldest due first (editable line by line); what is not spread stays as advance.
 */

interface Open {
  id: string;
  number: string;
  dueDate: string;
  remaining: number;
  pending: number;
  late: number;
  status: InvoiceStatus;
}
interface Ctx {
  today: string;
  customer: Cust;
  modes: Mode[];
  cashLimitB2B: number;
  open: Open[];
}

const r2 = (x: number) => Math.round(x * 100) / 100;
const num = (s: string) => parseFloat((s || '0').replace(/\s/g, '').replace(',', '.')) || 0;
const fr = (x: number) => (x ? String(r2(x)).replace('.', ',') : '');

export function suggest(open: Open[], amount: number): Record<string, string> {
  let left = amount;
  const out: Record<string, string> = {};
  for (const x of [...open]
    .filter((o) => o.status !== 'LITIGE')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))) {
    if (left <= 0.004) break;
    const take = r2(Math.min(left, x.remaining - x.pending));
    if (take > 0) out[x.id] = fr(take);
    left = r2(left - take);
  }
  return out;
}

export function RecordPaymentDialog({
  customerId: initialCustomer,
  invoiceId,
  invoiceIds,
  onClose,
  onDone,
}: {
  customerId?: string | null;
  invoiceId?: string | null;
  /** Several invoices the payer named (e.g. ticked in Documents): pre-filled in full. */
  invoiceIds?: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const named = (invoiceIds ?? (invoiceId ? [invoiceId] : [])).join(',');
  const { t } = useTranslation();
  const { push } = useToast();
  const [customerId, setCustomerId] = useState(initialCustomer ?? '');
  const [search, setSearch] = useState('');
  const [choices, setChoices] = useState<Array<Cust & { open: number }>>([]);
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [mode, setMode] = useState<Mode | ''>('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [due, setDue] = useState('');
  const [ref, setRef] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [err, setErr] = useState<{ msg: string; field?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const commandId = useRef(crypto.randomUUID()).current;

  useEffect(() => {
    if (initialCustomer) return;
    const h = setTimeout(() => {
      apiClient
        .get<Array<Cust & { open: number }>>(
          `/collection/payable-customers?q=${encodeURIComponent(search)}`
        )
        .then((r) => setChoices(r.data))
        .catch(() => setChoices([]));
    }, 200);
    return () => clearTimeout(h);
  }, [search, initialCustomer]);

  useEffect(() => {
    setCtx(null);
    setErr(null);
    if (!customerId) return;
    apiClient
      .get<Ctx>(`/collection/customers/${customerId}/payment-context`)
      .then((r) => {
        const c = r.data;
        setCtx(c);
        const m =
          c.customer.mode && c.modes.includes(c.customer.mode) ? c.customer.mode : c.modes[0];
        setMode(m);
        setDate(c.today);
        setDue('');
        setRef('');
        const targets = named ? c.open.filter((o) => named.split(',').includes(o.id)) : [];
        if (targets.length) {
          const pre: Record<string, string> = {};
          for (const o of targets) pre[o.id] = fr(r2(o.remaining - o.pending));
          setAmount(fr(r2(targets.reduce((a, o) => a + o.remaining - o.pending, 0))));
          setAlloc(pre);
        } else {
          setAmount('');
          setAlloc({});
        }
      })
      .catch((e) => setErr({ msg: apiMessage(t, e) }));
  }, [customerId, named, t]);

  // Mode-dependent defaults: an LCR's number and due date, a direct debit's mandate and date.
  useEffect(() => {
    if (!ctx || !mode) return;
    setErr(null);
    const first = Object.keys(alloc)[0] ?? ctx.open[0]?.id;
    const tag = ctx.open.find((o) => o.id === first)?.number.replace(/^[A-Z]+-/, '') ?? '';
    if (mode === 'LCR') {
      setDue((d) => d || addDays(ctx.today, 30));
      setRef((r) => (r && !r.startsWith('SEPA ') ? r : `LCR-${tag}`));
    } else if (mode === 'SEPA') {
      setDue((d) => d || ctx.today);
      setRef(ctx.customer.sepa ? `SEPA ${ctx.customer.sepa.rum}` : '');
    } else {
      setRef((r) => (/^(LCR|SEPA)/.test(r) ? '' : r));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ctx]);

  const total = num(amount);
  const spread = useMemo(() => r2(Object.values(alloc).reduce((a, v) => a + num(v), 0)), [alloc]);
  const left = r2(total - spread);
  const onAmount = (v: string) => {
    setAmount(v);
    if (ctx) setAlloc(suggest(ctx.open, num(v)));
  };

  const submit = async () => {
    if (!ctx || !mode) return;
    setBusy(true);
    setErr(null);
    try {
      const allocations = Object.entries(alloc)
        .map(([invoiceId, v]) => ({ invoiceId, amount: num(v) }))
        .filter((a) => a.amount > 0);
      const r = await apiClient.post<{ number: string; onAccount: number }>(
        '/collection/payments',
        {
          customerId,
          mode,
          amount: total,
          date,
          dueDate: mode === 'LCR' || mode === 'SEPA' ? due : null,
          reference: ref,
          allocations,
          commandId,
        },
        cmd(commandId)
      );
      push({
        tone: 'success',
        message:
          t('collection.pay.done', { number: r.data.number }) +
          (r.data.onAccount
            ? ` ${t('collection.pay.doneAdvance', { amount: formatEUR(r.data.onAccount) })}`
            : ''),
      });
      onDone();
      onClose();
    } catch (e) {
      const kind = (e as { response?: { data?: { details?: { kind?: string } } } })?.response?.data
        ?.details?.kind;
      const field = {
        REF: 'ref',
        DUE: 'due',
        AMOUNT: 'amount',
        CASH_LIMIT: 'amount',
        OVER: 'alloc',
        OVER_INVOICE: 'alloc',
        NO_MANDATE: 'mode',
        CASH_ACCOUNT: 'mode',
      }[kind ?? ''];
      setErr({ msg: apiMessage(t, e), field });
    } finally {
      setBusy(false);
    }
  };

  const bad = (f: string) => err?.field === f && 'border-destructive ring-2 ring-destructive/20';
  const help = mode
    ? (t(`collection.pay.help.${mode}`, { returnObjects: true }) as [string, string, string])
    : null;

  return (
    <Modal
      title={t('collection.pay.title')}
      lede={t('collection.pay.lede')}
      onClose={onClose}
      wide
      footer={
        <>
          <p
            className="mr-auto min-h-[1.2em] flex-1 text-sm font-semibold text-destructive"
            role="alert"
          >
            {err?.msg}
          </p>
          <Button variant="outline" onClick={onClose}>
            {t('collection.common.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || !ctx || !(total > 0)}>
            {t('collection.pay.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className={labelCls}>{t('collection.pay.customer')}</span>
          {initialCustomer ? (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">
              {ctx?.customer.name ?? '…'}
            </div>
          ) : (
            <div className="grid gap-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('collection.pay.customerSearch')}
                aria-label={t('collection.pay.customerSearch')}
              />
              <Select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                aria-label={t('collection.pay.customer')}
              >
                <option value="">{t('collection.pay.pickCustomer')}</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {t(`collection.type.${c.type}`)}
                    {c.open ? ` · ${formatEUR(c.open)}` : ''}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
        <label className="block">
          <span className={labelCls}>{t('collection.pay.mode')}</span>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
            disabled={!ctx}
            className={cn(bad('mode'))}
          >
            {(ctx?.modes ?? []).map((m) => (
              <option key={m} value={m}>
                {t(`collection.mode.${m}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="block">
          <span className={labelCls}>{t('collection.pay.amount')}</span>
          <Input
            inputMode="decimal"
            placeholder="0,00"
            value={amount}
            onChange={(e) => onAmount(e.target.value)}
            disabled={!ctx}
            className={cn(bad('amount'))}
          />
        </label>
        <label className="block">
          <span className={labelCls}>{t('collection.pay.date')}</span>
          <Input
            type="date"
            value={date}
            max={ctx?.today}
            onChange={(e) => setDate(e.target.value)}
            disabled={!ctx}
          />
        </label>
        {(mode === 'LCR' || mode === 'SEPA') && (
          <label className="block">
            <span className={labelCls}>
              {t(mode === 'LCR' ? 'collection.pay.dueLcr' : 'collection.pay.dueSepa')}
            </span>
            <Input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className={cn(bad('due'))}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              {mode === 'LCR'
                ? t('collection.pay.dueLcrHint')
                : ctx?.customer.sepa
                  ? t('collection.pay.mandate', {
                      rum: ctx.customer.sepa.rum,
                      date: formatDate(ctx.customer.sepa.signedAt),
                    })
                  : t('collection.pay.noMandate')}
            </span>
          </label>
        )}
        <label className="block">
          <span className={labelCls}>{help?.[0] ?? t('collection.pay.reference')}</span>
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            disabled={!ctx || mode === 'SEPA'}
            className={cn(bad('ref'))}
          />
          {help?.[1] && <span className="mt-1 block text-xs text-muted-foreground">{help[1]}</span>}
        </label>
      </div>
      {help?.[2] && (
        <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">{help[2]}</div>
      )}
      {ctx && (
        <div>
          <span className={labelCls}>{t('collection.pay.allocation')}</span>
          {ctx.open.length === 0 ? (
            <p className="rounded-md border px-3 py-4 text-center text-sm text-muted-foreground">
              {t('collection.pay.noOpen')}
            </p>
          ) : (
            <div className={cn('overflow-x-auto rounded-md border', bad('alloc'))}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-1.5 text-left font-medium">
                      {t('collection.col.invoice')}
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium">{t('collection.col.due')}</th>
                    <th className="px-3 py-1.5 text-right font-medium">
                      {t('collection.pay.toCover')}
                    </th>
                    <th className="px-3 py-1.5 text-right font-medium">
                      {t('collection.pay.allocate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ctx.open.map((o) => (
                    <tr key={o.id} className="border-b last:border-b-0">
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {o.number}
                        {o.status === 'LITIGE' && (
                          <span className="ml-2 font-sans text-xs text-primary">
                            {t('collection.status.LITIGE')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {formatDate(o.dueDate)}
                        {o.late > 0 && (
                          <span className="ml-2 rounded bg-warning/[.13] px-1.5 text-xs font-semibold text-warning">
                            {t('collection.common.days', { n: o.late })}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatEUR(o.remaining - o.pending)}
                      </td>
                      <td className="w-32 px-3 py-1.5">
                        <Input
                          inputMode="decimal"
                          className="h-8 text-right"
                          value={alloc[o.id] ?? ''}
                          onChange={(e) => setAlloc((a) => ({ ...a, [o.id]: e.target.value }))}
                          aria-label={t('collection.pay.allocateTo', { number: o.number })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p
            className={cn(
              'pt-1.5 text-sm',
              left < 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'
            )}
          >
            {left > 0.004
              ? t('collection.pay.leftAdvance', { amount: formatEUR(left) })
              : left < -0.004
                ? t('collection.pay.over', { amount: formatEUR(-left) })
                : total > 0
                  ? t('collection.pay.allSpread')
                  : ''}
          </p>
        </div>
      )}
    </Modal>
  );
}

function addDays(s: string, n: number): string {
  const d = new Date(`${s}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
